/**
 * The stage stepper.
 *
 * Ordered by `sort_order`, never by the code — §5.1 [R3]: `stage_code` is
 * unordered text and every ordering question in this product goes through the
 * `stages` table. §5.8 also permits skips and regressions, so "reached" means
 * `sort_order <= current`, not "the previous step in this array".
 */

import { Check } from 'lucide-react';
import { useStages } from '@/lib/reference';
import { cn } from '@/lib/utils';

export function StageStepper({
  stageCode,
  compact = false,
}: {
  stageCode: string;
  compact?: boolean;
}): React.ReactElement {
  const { list, byCode } = useStages();
  const current = byCode.get(stageCode);
  const currentOrder = current?.sort_order ?? 0;

  return (
    <ol className="flex flex-wrap items-center gap-1" aria-label="Stage">
      {list.map((stage) => {
        const reached = stage.sort_order < currentOrder;
        const isCurrent = stage.code === stageCode;
        return (
          <li key={stage.code}>
            <span
              aria-current={isCurrent ? 'step' : undefined}
              title={stage.entry_criteria ?? stage.name}
              className={cn(
                'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-tight',
                isCurrent && 'border-primary bg-primary text-primary-foreground font-medium',
                reached && !isCurrent && 'border-emerald-600/40 bg-emerald-50 text-emerald-900',
                !reached && !isCurrent && 'border-input text-muted-foreground',
              )}
            >
              {reached && !isCurrent && <Check className="h-3 w-3" aria-hidden="true" />}
              {compact ? stage.name.split(' ')[0] : stage.name}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** A one-cell progress indicator for the list view. */
export function StageProgress({
  stageCode,
  stageName,
  sortOrder,
}: {
  stageCode: string;
  stageName?: string | undefined;
  sortOrder?: number | undefined;
}): React.ReactElement {
  const { list, byCode } = useStages();
  const stage = byCode.get(stageCode);
  const order = sortOrder ?? stage?.sort_order ?? 0;
  const maxOrder = list[list.length - 1]?.sort_order ?? 80;
  const percent = maxOrder === 0 ? 0 : Math.round((order / maxOrder) * 100);

  return (
    <span className="flex items-center gap-2">
      <span
        className="h-1.5 w-12 shrink-0 rounded-full bg-muted"
        role="img"
        aria-label={`${percent}% through the funnel`}
      >
        <span className="block h-1.5 rounded-full bg-primary" style={{ width: `${percent}%` }} />
      </span>
      <span className="truncate">{stageName ?? stage?.name ?? stageCode}</span>
    </span>
  );
}
